import { useMemo, useState } from "react";
import { AnalyticsPlatformParams, AnalyticsRangeParams } from "../../../../model/bps-admin/business-analytics";
import { useGetUtilityConfigurationsQuery } from "../../../../service/utility-configuration-service";

// The utilities list is paginated; there is no lookup endpoint, and five utilities
// exist today, so one page wide enough to hold them all stands in for one.
const UTILITY_PAGE_SIZE = 100;

/** Sentinel for "do not filter by utility" — the endpoint's own ids are Guids. */
export const ALL_UTILITIES = "all";

export const ALL_PLATFORMS = "all";

// The platform-users endpoint also returns an "other" bucket, which is deliberately
// neither offered here nor drawn — those users appear in no series.
export const PLATFORM_OPTIONS = [
	{ value: ALL_PLATFORMS, label: "All platforms" },
	{ value: "web", label: "Web" },
	{ value: "android", label: "Android" },
	{ value: "ios", label: "iOS" },
];

export const PERIOD_OPTIONS = [
	{ value: "7d", label: "Last 7 days" },
	{ value: "30d", label: "Last 30 days" },
	{ value: "90d", label: "Last 90 days" },
	{ value: "12m", label: "Last 12 months" },
];

// How many days back each period reaches. The endpoints return a row per day, so a
// year is ~365 rows rather than 12 monthly ones.
const PERIOD_DAYS: Record<string, number> = { "7d": 7, "30d": 30, "90d": 90, "12m": 365 };

// Built from the local date parts: toISOString would convert to UTC first and move
// the range a day in negative offsets.
const isoDay = (date: Date) =>
	`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const periodRange = (period: string) => {
	const from = new Date();
	from.setDate(from.getDate() - (PERIOD_DAYS[period] - 1));
	return { dateFrom: isoDay(from), dateTo: isoDay(new Date()) };
};

export interface AnalyticsFilterOption {
	value: string;
	label: string;
}

/** Everything AnalyticsFilters needs to draw itself — spread it straight in. */
export interface AnalyticsFilterControls {
	utility: string;
	setUtility: (value: string) => void;
	platform: string;
	setPlatform: (value: string) => void;
	period: string;
	setPeriod: (value: string) => void;
	utilityOptions: AnalyticsFilterOption[];
}

/**
 * The filter state every analytics page carries, plus the slice it resolves to.
 * One hook per page: each page scopes its own widgets, and nothing is shared
 * between them, so leaving one page does not carry its period into the next.
 */
export const useAnalyticsFilters = () => {
	const [utility, setUtility] = useState(ALL_UTILITIES);
	const [platform, setPlatform] = useState(ALL_PLATFORMS);
	const [period, setPeriod] = useState("30d");

	const { data: utilities } = useGetUtilityConfigurationsQuery({ page: 1, pageSize: UTILITY_PAGE_SIZE });

	const utilityOptions = useMemo(
		() => [
			{ value: ALL_UTILITIES, label: "All utilities" },
			...(utilities?.items ?? []).map(item => ({ value: item.id, label: item.utilityName })),
		],
		[utilities]
	);

	// One slice for the whole page, so every figure on it agrees with every other.
	const range: AnalyticsRangeParams = {
		...periodRange(period),
		utilityId: utility === ALL_UTILITIES ? undefined : utility,
	};

	// For the endpoints that narrow by platform too. Kept apart from `range` rather than
	// folded into it: platform-users takes no platform, and would otherwise be re-keyed —
	// and so refetched — every time the filter moved between platforms it ignores.
	const rangeWithPlatform: AnalyticsPlatformParams = {
		...range,
		platform: platform === ALL_PLATFORMS ? undefined : platform,
	};

	return {
		utility,
		platform,
		period,
		range,
		rangeWithPlatform,
		periodLabel: PERIOD_OPTIONS.find(option => option.value === period)?.label ?? "",
		platformLabel: PLATFORM_OPTIONS.find(option => option.value === platform)?.label ?? "",
		controls: { utility, setUtility, platform, setPlatform, period, setPeriod, utilityOptions },
	};
};
